<template>
  <Teleport v-if="isDesktop && cities.length" to="#app-header-tools">
    <div class="calendar-city-select calendar-city-select--header">
      <CityOfficeSelect v-model="selectedBranchId" :allow-all="true" />
    </div>
  </Teleport>
  <div class="calendar-container">
    <div v-if="!isDesktop && cities.length" class="calendar-city-select calendar-city-select--mobile">
      <CityOfficeSelect v-model="selectedBranchId" :allow-all="true" />
    </div>
    <el-alert v-if="calendarError" :title="calendarError" type="error" :closable="false" />
    <div v-else-if="!calendarLoading && !schedules.some(u => u.shifts?.length)" class="working-time-empty">
      <span>Рабочее время ещё не добавлено</span>
      <el-button v-if="canAddWorkTime" type="primary" @click="workTimeOpen = true">Добавить рабочее время</el-button>
    </div>
    <CreateScheduleDialog v-model="workTimeOpen" @saved="loadCalendar" />
    <div class="calendar-header">
      <div class="date-header">
        <DateColumn v-model="selectedDate" />
      </div>
      <div class="header-scroll-x" ref="headerScroll">
        <HeaderCabinetsAndEmployees
          :rooms-with-employees="roomsWithEmployees"
          :emp-col-width="empColWidth"
          :scrollbar-width="scrollbarWidth"
          :fallback-email="user?.email || ''"
        />
      </div>
    </div>
    <div class="calendar-main-scroll" ref="mainScroll" @scroll="onGridScroll">
      <div class="time-scale-column" ref="timeScaleColumn">
        <TimeScale :marks="timeMarks" :slot-height="slotHeight" />
      </div>
      <div class="grid-column">
        <ScheduleGrid
          :rooms-with-employees="roomsWithEmployees"
          :time-marks="timeMarks"
          :emp-col-width="empColWidth"
          :slot-height="slotHeight"
          :appointments="appointmentsForCurrentBranch"
          :current-date="selectedDate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CreateScheduleDialog from '@/components/util/scheduler/CreateScheduleDialog.vue'
import { hasPermission } from '@/components/util/accessHelper'
import DateColumn from '@/components/util/calendar/DateColumn.vue'
import TimeScale from '@/components/util/calendar/TimeScale.vue'
import ScheduleGrid from '@/components/util/calendar/ScheduleGrid.vue'
import HeaderCabinetsAndEmployees from '@/components/util/calendar/HeaderCabinetsAndEmployees.vue'
import CityOfficeSelect from '@/components/util/calendar/CityOfficeSelect.vue'
import { mapActions, mapState } from 'vuex'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Calendar',
  components: {
    CreateScheduleDialog,
    CityOfficeSelect,
    DateColumn,
    TimeScale,
    ScheduleGrid,
    HeaderCabinetsAndEmployees
  },
  data() {
    return {
      scrollbarWidth: 0,
      gridScrollLeft: 0,
      selectedBranchId: null,
      selectedDate: new Date().toISOString().slice(0, 10),
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      lastAutoScrollKey: '',
      workTimeOpen: false,
      calendarError: '',
      calendarLoading: false,
      calendarRequest: 0
    }
  },
  computed: {
    ...mapState('cities', ['cities']),
    ...mapState('auth', ['user']),
    ...mapState('schedules', ['schedules']),
    ...mapState('appointments', ['appointments']),
    canAddWorkTime() { return hasPermission('schedule.create') },
    isDesktop() {
      return this.viewportWidth >= 992
    },
    empColWidth() {
      if (this.viewportWidth <= 480) return 252
      if (this.viewportWidth <= 768) return 236
      if (this.viewportWidth < 992) return 252
      return 180
    },
    slotHeight() {
      return this.viewportWidth >= 992 ? 38 : 31
    },
    isSingleLocation() {
      if (!this.cities || !this.cities.length) return false
      const branches = this.cities.flatMap(c => c.children || [])
      if (branches.length !== 1) return false
      const rooms = branches[0].children || []
      return rooms.length === 1
    },
    appointmentsForCurrentBranch() {
      if (!this.selectedDate) return []
      return (this.appointments || []).filter(a =>
        (!this.selectedBranchId || Number(a.branch_id) === Number(this.selectedBranchId)) &&
        a.date === this.selectedDate
      )
    },
    currentBranch() {
      let found = null
      for (const city of this.cities) {
        if (!city.children) continue
        const branch = city.children.find(b => b.id === this.selectedBranchId)
        if (branch) {
          found = branch
          break
        }
      }
      return found
    },
    currentCityId() {
      for (const city of this.cities) {
        if (!city.children) continue
        const branch = city.children.find(b => b.id === this.selectedBranchId)
        if (branch) return city.id
      }
      return null
    },
    roomsWithEmployees() {
      const employees = [...(this.schedules || [])]
      for (const appointment of this.appointmentsForCurrentBranch) {
        if (!employees.some(u => Number(u.id) === Number(appointment.user_id))) employees.push({ id: appointment.user_id, name: appointment.user_name, shifts: [] })
      }
      return employees.map(emp => ({
        key: emp.id,
        employees: [{
          user_id: emp.id, user_name: emp.name, avatar: emp.avatar,
          city_id: emp.city_id, branch_id: emp.branch_id, room_id: emp.room_id,
          shifts: emp.shifts || [], breaks: (emp.shifts || []).flatMap(s => s.breaks || []),
        }],
      }))
    },
    timeMarks() {
      const { start = '08:00', end = '21:00' } = this.currentBranch?.working || {}
      const [h1, m1] = start.split(':').map(Number)
      const [h2, m2] = end.split(':').map(Number)
      const minute = value => { const [h, m] = String(value).split(':').map(Number); return h * 60 + m }
      const shifts = (this.schedules || []).flatMap(u => u.shifts || [])
      const startMin = Math.min(h1 * 60 + m1, ...shifts.map(s => minute(s.start_time)))
      const endMin = Math.max(h2 * 60 + m2, ...shifts.map(s => minute(s.end_time)))
      const arr = []
      const interval = Math.max(5, Number(this.user?.cell_interval) || 15)
      for (let t = Math.floor(startMin / interval) * interval; t <= Math.ceil(endMin / interval) * interval; t += interval) arr.push(t)
      return arr
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (!newUser) return
        const payload = newUser.is_admin ? {} : { user_id: newUser.id }
        this.getCities(payload).then(() => {
          this.loadCalendar()
        }).catch(e => { this.calendarError = e.message || 'Ошибка загрузки календаря' })
        this.getUsers()
      }
    },
    selectedBranchId() { this.loadCalendar() },
    selectedDate() { this.loadCalendar() },
    timeMarks() {
      this.$nextTick(() => this.scheduleAutoScroll())
    }
  },
  mounted() {
    this.scrollbarWidth = this.getScrollbarWidth()
    this.fetchCustomFields({ entity: 'appointment' })
    window.addEventListener('resize', this.handleResize, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions('cities', ['getCities']),
    ...mapActions('users', ['getUsers']),
    ...mapActions('schedules', ['fetchCalendarSchedules']),
    ...mapActions('services', ['fetchServices']),
    ...mapActions('appointments', ['fetchAppointments']),
    ...mapActions('custom_fields', ['fetchCustomFields']),
    async loadCalendar() {
      if (!this.user?.id) return
      const request = ++this.calendarRequest
      this.calendarLoading = true
      this.calendarError = ''
      try {
        await this.fetchCalendarSchedules({ date: this.selectedDate, city_id: this.currentCityId, branch_id: this.selectedBranchId })
        if (request !== this.calendarRequest) return
        await this.fetchAppointments({ date: this.selectedDate, city_id: this.currentCityId, branch_id: this.selectedBranchId })
        if (this.currentCityId) await this.fetchServices({ city_id: this.currentCityId })
      } catch (e) { if (request === this.calendarRequest) this.calendarError = e.message || 'Ошибка загрузки календаря' }
      finally { if (request === this.calendarRequest) this.calendarLoading = false }
    },
    handleResize() {
      this.viewportWidth = window.innerWidth
    },
    getMaxScrollTopByTimeScale(containerEl) {
      const timeScaleEl = this.$refs.timeScaleColumn
      if (!containerEl || !timeScaleEl) return 0
      return Math.max(0, timeScaleEl.scrollHeight - containerEl.clientHeight)
    },
    clampVerticalScrollToTimeScale() {
      const containerEl = this.$refs.mainScroll
      if (!containerEl) return
      const maxScrollTop = this.getMaxScrollTopByTimeScale(containerEl)
      if (containerEl.scrollTop > maxScrollTop) {
        containerEl.scrollTop = maxScrollTop
      }
    },
    scheduleAutoScroll() {
      if (!this.selectedBranchId || !this.timeMarks.length) return
      const autoScrollKey = `${this.selectedBranchId}:${this.selectedDate}:${this.timeMarks[0]}:${this.timeMarks[this.timeMarks.length - 1]}`
      if (this.lastAutoScrollKey === autoScrollKey) return
      this.scrollToCurrentTime(autoScrollKey)
    },
    scrollToCurrentTime(autoScrollKey = '') {
      const containerEl = this.$refs.mainScroll
      if (!containerEl || !this.timeMarks.length) return

      const now = new Date()
      const currentMinutes = now.getHours() * 60 + now.getMinutes()
      let closestIndex = 0

      for (let i = 0; i < this.timeMarks.length; i += 1) {
        if (this.timeMarks[i] <= currentMinutes) {
          closestIndex = i
        } else {
          break
        }
      }

      const targetTop = Math.max(0, closestIndex * this.slotHeight)
      const maxScrollTop = this.getMaxScrollTopByTimeScale(containerEl)
      containerEl.scrollTop = Math.min(targetTop, maxScrollTop)
      this.lastAutoScrollKey = autoScrollKey
    },
    onGridScroll(e) {
      const maxScrollTop = this.getMaxScrollTopByTimeScale(e.target)
      if (e.target.scrollTop > maxScrollTop) {
        e.target.scrollTop = maxScrollTop
      }
      this.gridScrollLeft = e.target.scrollLeft
      if (this.$refs.headerScroll) {
        this.$refs.headerScroll.scrollLeft = this.gridScrollLeft
      }
    },
    getScrollbarWidth() {
      const outer = document.createElement('div')
      outer.style.visibility = 'hidden'
      outer.style.width = '100px'
      outer.style.msOverflowStyle = 'scrollbar'
      document.body.appendChild(outer)

      const widthNoScroll = outer.offsetWidth
      outer.style.overflow = 'scroll'

      const inner = document.createElement('div')
      inner.style.width = '100%'
      outer.appendChild(inner)

      const widthWithScroll = inner.offsetWidth
      outer.parentNode.removeChild(outer)
      return widthNoScroll - widthWithScroll
    }
  },
  updated() {
    this.clampVerticalScrollToTimeScale()
  }
}
</script>

<style scoped>
.calendar-container {
  --ui-color-bg-base: var(--cal-bg-panel);
  --ui-color-bg-weak: var(--cal-bg-app);
  --ui-color-bg-card: var(--cal-bg-panel-alt);
  --ui-color-white: var(--cal-bg-panel-alt);
  --ui-color-white-100: var(--cal-bg-panel-alt);
  --ui-color-border-default: var(--cal-border);
  --ui-color-border-soft: var(--cal-border-soft);
  --ui-color-text-primary: var(--cal-text-primary);
  --ui-color-text-regular: var(--cal-text-regular);
  --ui-color-text-secondary: var(--cal-text-muted);
  --ui-color-text-muted: var(--cal-text-muted);
  --ui-color-text-dim: var(--cal-text-dim);
  --ui-color-black: var(--cal-text-primary);
  --calendar-header-block-height: 72px;

  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  min-height: 0;
  background: var(--lineon-bg);
  box-sizing: border-box;
  padding-left: 10px;
}

.calendar-city-select {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.calendar-city-select--header {
  max-width: 356px;
}

.calendar-city-select--mobile {
  padding: 10px 10px 0;
}

.calendar-header {
  display: flex;
  align-items: flex-start;
  width: 100%;
  background: transparent;
  padding-top: 0;
  padding-bottom: 8px;
}

.date-header {
  width: 88px;
  min-width: 88px;
  max-width: 88px;
  background: transparent;
}

.header-scroll-x {
  flex: 1 1 0;
  overflow-x: hidden;
  height: var(--calendar-header-block-height);
  min-height: var(--calendar-header-block-height);
}

.calendar-main-scroll {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  padding-top: 0;
  background: transparent;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.calendar-main-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.time-scale-column {
  width: 88px;
  min-width: 88px;
  max-width: 88px;
  background: transparent;
  flex-shrink: 0;
}

.grid-column {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  background: transparent;
}

.calendar-container :deep(.el-select__wrapper),
.calendar-city-select :deep(.el-select__wrapper) {
  background: #06122c;
  box-shadow: inset 0 0 0 1px rgba(0, 196, 255, 0.7);
  border-radius: 5px;
  min-height: 32px;
  height: 32px;
  width: 356px;
  padding-left: 10px;
  padding-right: 13px;
}

.calendar-container :deep(.el-select__placeholder),
.calendar-container :deep(.el-select__selected-item),
.calendar-city-select :deep(.el-select__placeholder),
.calendar-city-select :deep(.el-select__selected-item) {
  color: #00c4ff !important;
  font-size: 17px !important;
  font-weight: 300 !important;
  line-height: 1 !important;
}

.calendar-container :deep(.el-select__caret),
.calendar-city-select :deep(.el-select__caret) {
  color: transparent !important;
}

.calendar-container :deep(.el-input__wrapper) {
  background: var(--cal-bg-panel-alt);
  box-shadow: 0 0 0 1px var(--cal-border-soft) inset;
}

.calendar-container :deep(.el-select-group__title) {
  color: var(--cal-text-muted);
}

.calendar-container :deep(.el-select-dropdown__item.is-selected) {
  color: var(--cal-accent-strong);
  font-weight: 300;
}

@media (max-width: 768px) {
  .calendar-container {
    padding-left: 0;
  }

  .calendar-header {
    padding-bottom: 0;
  }

  .calendar-city-select {
    justify-content: stretch;
  }

  .calendar-container :deep(.city-office-select) {
    width: 100%;
  }

  .calendar-container :deep(.city-office-select .el-select) {
    width: 100% !important;
    max-width: none !important;
  }

  .calendar-container :deep(.city-office-select .el-select__wrapper),
  .calendar-container :deep(.el-select__wrapper) {
    width: 100% !important;
  }

  .calendar-header {
    padding-top: 2px;
  }

  .date-header,
  .time-scale-column {
    width: 92px;
    min-width: 92px;
    max-width: 92px;
  }

  .header-scroll-x {
    min-height: 78px;
  }

  .calendar-container :deep(.date-column) {
    width: 92px;
    height: 78px;
    padding: 0 4px 0;
    gap: 7px;
  }

  .calendar-container :deep(.date-card) {
    flex-basis: 41px;
    min-height: 41px;
    padding: 5px 4px 3px;
  }

  .calendar-container :deep(.date-big),
  .calendar-container :deep(.date-current-time) {
    font-size: 15px;
  }

  .calendar-container :deep(.date-current-time) {
    height: 30px;
    line-height: 30px;
  }

  .calendar-container :deep(.cabinet-cell) {
    height: 42px;
    padding: 0 10px;
    font-size: 15px;
  }

  .calendar-container :deep(.room-group) {
    height: 78px;
  }

  .calendar-container :deep(.employee-cell) {
    height: 36px;
    padding: 0 10px;
  }

  .calendar-container :deep(.employee-content) {
    gap: 6px;
    padding: 0 10px;
  }

  .calendar-container :deep(.employee-name) {
    max-width: calc(100% - 24px);
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .calendar-city-select--mobile {
    padding: 8px 8px 0;
  }

  .date-header,
  .time-scale-column {
    width: 78px;
    min-width: 78px;
    max-width: 78px;
  }

  .calendar-container :deep(.date-column) {
    width: 78px;
    height: 78px;
    padding: 0 2px 0;
    gap: 8px;
  }

  .calendar-container :deep(.date-card) {
    flex-basis: 40px;
    min-height: 40px;
    padding: 5px 2px 3px;
  }

  .calendar-container :deep(.date-big),
  .calendar-container :deep(.date-current-time) {
    font-size: 13px;
  }

  .calendar-container :deep(.date-weekday) {
    font-size: 9px;
  }

  .calendar-container :deep(.time-scale) {
    width: 76px;
    padding: 0 2px;
  }

  .calendar-container :deep(.mark-hour .time-label) {
    font-size: 15px;
  }

  .calendar-container :deep(.mark-half .time-label) {
    font-size: 12px;
  }

  .calendar-container :deep(.cabinet-cell) {
    padding: 0 6px;
    font-size: 12px;
  }

  .calendar-container :deep(.employee-cell) {
    padding: 0 6px;
  }

  .calendar-container :deep(.employee-name) {
    max-width: none;
    font-size: 9px;
  }

  .calendar-container :deep(.employee-avatar) {
    width: 18px !important;
    height: 18px !important;
    font-size: 9px !important;
  }
}
</style>

<style scoped>
.working-time-empty { display: flex; gap: 16px; align-items: center; padding: 12px; flex-wrap: wrap; }
</style>
